/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { getEnvironment } from 'src/core/utils/api/environment';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation, getPortalFromUrl } from 'src/core/utils/routingUtils';
import { UserMigrationInfoSwagger } from 'src/verticals/azureMigrationModal/services/azureMigrationSwagger';

/**
 * Represents a service for authenticating the user
 * using the new authentication implementation in
 * LuisWebPortal.
 */
export const getAuthService = () => {
    const { authWebApiUrl } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useCORS()
        .useContentType('json')
        .build();

    /**
     * Redirects the page to the given url.
     *
     * @param url The url to redirect to.
     */
    const goToUrl = (url: string): void => window.location.assign(url);

    /// Helper function to get URLSearchParams object with case insensitive keys
    const getURLSearchParamsCaseInsensitive = (searchString: string): URLSearchParams => {
        const queryCaseSensitive = new URLSearchParams(searchString);
        const queryCaseInsensitive = new URLSearchParams();
        queryCaseSensitive.forEach((value, key) => {
            queryCaseInsensitive.append(key.toLowerCase(), value);
        });
        return queryCaseInsensitive;
    };

    const getRedirectUrl = (): string => {
        const portal = getPortalFromUrl();
        const { origin, pathname, href } = window.location;
        const isInSessionExpiredPage = doesRouteMatchLocation(routeTable.sessionExpiredPage(), { locationToMatch: pathname });

        if (portal.kind === 'demos') {
            return href;
        }

        if (portal.kind === 'luis' || isInSessionExpiredPage) {
            return origin;
        }

        switch (portal.subPath) {
            case 'clu':
                return `${origin}${routeTable.clu.projectsPage()}`;
            case 'customText':
                return `${origin}${
                    portal.serviceName === 'classification'
                        ? routeTable.customText.classificationProjectsPage()
                        : routeTable.customText.extractionProjectsPage()
                }`;
            case 'qnaMaker':
                return `${origin}${routeTable.qnaMaker.projectsPage()}`;
            case 'orchestration':
                return `${origin}${routeTable.orchestration.projectsPage()}`;
            case 'translation':
                return `${origin}${routeTable.translation.root()}`;
            case 'luis':
                return `${origin}${routeTable.luis.root()}`;
            default:
                return href;
        }
    };
    /**
     * Logs in to LUIS.
     *
     * @param tenantId An optional tenant id to login to.
     */
    const login = ({ tenantId, selectAccount }: { tenantId?: string; selectAccount?: boolean } = { selectAccount: false }): void => {
        const query = getURLSearchParamsCaseInsensitive(window.location.search);
        const tenantIdFromSearch = query.get('tenantid') || undefined;

        const identityUrl = urlBuilder()
            .baseUrl(authWebApiUrl)
            .resource('identity')
            .resource('account')
            .resource('signIn')
            // It has to be the service origin because of the following case.
            // If user opens a specific project in any service then s/he switches
            // the tenant the "href" will try to get him/her to the same project which isn't
            // valid because of different tenants, subscriptions and resources.
            .queryParam('redirectUrl', getRedirectUrl());

        if (tenantId) {
            identityUrl.queryParam('tenantId', tenantId);
        }
        if (tenantIdFromSearch) {
            identityUrl.queryParam('tenantId', tenantIdFromSearch);
        }

        if (selectAccount) {
            identityUrl.queryParam('prompt', 'select_account');
        }

        goToUrl(identityUrl.build());
    };

    /**
     * Signs out from LUIS.
     */
    const logout = (): void => {
        const url = urlBuilder()
            .baseUrl(authWebApiUrl)
            .resource('identity')
            .resource('account')
            .resource('signOut')
            .queryParam('redirectUrl', window.location.origin)
            .build();

        goToUrl(url);
    };

    /**
     * Checks if the user is logged in or not.
     *
     * @returns Information regarding the user AAD session.
     */
    const checkLoginStatus = async () => {
        const url = urlBuilder()
            .baseUrl(authWebApiUrl)
            .resource('api')
            .resource('auth')
            .resource('authInfo')
            .build();

        const { data: authInfo } = await httpService.get<{
            id: string;
            name: string;
            email: string;
            photo: string;
            defaultTenantId: string;
        }>(url, apiOptions);

        return authInfo;
    };

    /**
     * Get list of regions and the corresponding service keys which the user
     * have a trial account in.
     * This method will return empty list in case user has no account in Luis
     * or the user has been fully migrated from all regions.
     */
    const getUserMigrationInfo = async (): Promise<UserMigrationInfoSwagger[]> => {
        const options = luisRequestOptionsBuilder()
            .useCORS()
            .useContentType('json')
            .build();

        const url = urlBuilder()
            .baseUrl(authWebApiUrl)
            .resource('api')
            .resource('auth')
            .resource('getUserMigrationInfo')
            .build();

        const { data: UserMigrationInfo } = await httpService.get<UserMigrationInfoSwagger[]>(url, options);

        return UserMigrationInfo;
    };

    return { login, logout, checkLoginStatus, getUserMigrationInfo, getURLSearchParamsCaseInsensitive };
};
